import type { ButtonHTMLAttributes, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

type Variant = 'primary' | 'secondary' | 'ghost' | 'subtle' | 'danger'
type Size = 'sm' | 'md' | 'icon' | 'icon-sm'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: Variant
    size?: Size
    children?: ReactNode
}

const VARIANTS: Record<Variant, string> = {
    primary: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 disabled:bg-primary/50',
    secondary: 'border border-border bg-card text-foreground shadow-sm hover:bg-muted disabled:text-muted-foreground',
    ghost: 'text-muted-foreground hover:bg-muted hover:text-foreground',
    subtle: 'bg-accent text-accent-foreground hover:bg-accent/80',
    danger: 'border border-border bg-card text-rag-red-ink hover:bg-rag-red/10'
}

const SIZES: Record<Size, string> = {
    sm: 'h-8 gap-1.5 rounded-lg px-2.5 text-xs',
    md: 'h-9 gap-2 rounded-xl px-3.5 text-sm',
    icon: 'size-9 rounded-xl',
    'icon-sm': 'size-8 rounded-lg'
}

export const Button: FC<ButtonProps> = ({ variant = 'secondary', size = 'md', className, type = 'button', children, ...props }): ReactNode => {
    return (
        <button
            type={type}
            className={cn(
                'inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed',
                VARIANTS[variant],
                SIZES[size],
                className
            )}
            {...props}
        >
            {children}
        </button>
    )
}
